import { useState, useEffect, useMemo, useCallback } from 'react';
import { addDays, format, isAfter, parseISO } from 'date-fns';
import { Card, InputGroup, Form, ListGroup } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Checkbox } from '@Components/FormCheck';
import { Design, Size } from '@Components/designConstants';
import {
  awaitJSON,
  SheetsLambdaFetch,
  StreamingV2LambdaFetch,
  CreativeLambdaFetch,
  StreamingUtilsLambdaFetch,
  fetchPresigned
} from '../../../utils/fetch-utils';
import { DerivedNetwork } from '../../../redux/networks';
import { useMap } from '../../../utils/hooks/useData';
import { emailSelector } from '../../../redux/user';
import { useAppSelector } from '../../../redux';
import { AdOpsToolProps, isS3Redirect } from '../adops-constants';

interface DerivedIdInfo {
  newCount?: number;
  newCost?: number;
}

// Parse a numeric input, returning undefined for blank/non-numeric values so a partially typed
// row never stores NaN (which JSON-serializes to null and silently corrupts the transfer payload).
const parseNumericInput = (raw: string, parser: (value: string) => number): number | undefined => {
  if (raw === '') return undefined;
  const parsed = parser(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const TransferSpendAndImpressions = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const { setError } = utils;

  const email = useAppSelector(emailSelector);

  const [dates, setDates] = useState<DateRange>();
  const [dateList, setDateList] = useState<string[]>([]);

  const [notes, setNotes] = useState('');

  const [company, setCompany] = useState('');
  const [companyOptions, setCompanyOptions] = useState<string[]>();

  useEffect(() => {
    if (!companyOptions) {
      (async () => {
        try {
          const res = await SheetsLambdaFetch('/get_company_ids');
          const companies = await awaitJSON<{ ids: string[] }>(res);
          setCompanyOptions(companies.ids);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [companyOptions, setError]);

  const [network, setNetwork] = useState('');
  const [networkTo, setNetworkTo] = useState('');
  const [derivedNetworkMap, setDerivedNetworkMap] = useState<Record<string, DerivedNetwork>>({});

  useEffect(() => {
    if (company) {
      (async () => {
        try {
          const lambdaRes = await StreamingV2LambdaFetch('/derived', { params: { company } });
          const lambdaResult = await awaitJSON<Record<string, DerivedNetwork>>(lambdaRes);
          if (isS3Redirect(lambdaResult)) {
            const S3Res = await fetchPresigned(
              lambdaResult.url,
              'Derived networks download failed'
            );
            const derivedNetworks = await awaitJSON<Record<string, DerivedNetwork>>(S3Res);
            setDerivedNetworkMap(derivedNetworks);
          } else {
            setDerivedNetworkMap(lambdaResult);
          }
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [company, setError]);

  const derivedIdOptions = useMemo(() => Object.keys(derivedNetworkMap), [derivedNetworkMap]);

  const [isciOptions, setIsciOptions] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (company) {
      (async () => {
        try {
          const res = await CreativeLambdaFetch('/', {
            params: {
              company,
              timeline: 1,
              include_retired: true
            }
          });
          setIsciOptions(await awaitJSON<Record<string, unknown>>(res));
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [company, setError]);

  const isciOptionList = useMemo(() => Object.keys(isciOptions), [isciOptions]);

  const [creative, setCreative] = useState('');
  const [derivedIdMap, setDerivedIdMapValue, setDerivedIdMap] = useMap<string, DerivedIdInfo>({});
  const [derivedIdChange, setDerivedIdChange] = useState(false);

  const transferSpendAndImpressions = useCallback(async () => {
    try {
      setActiveAPICall(true);
      const params = derivedIdChange
        ? JSON.stringify({ dateList, network, networkTo, notes, email, derivedIdChange })
        : JSON.stringify({
            dateList,
            company,
            network,
            creative,
            derivedIdMap,
            notes,
            email,
            derivedIdChange
          });
      await StreamingUtilsLambdaFetch('/transferSpendAndImpressions', {
        method: 'POST',
        body: params
      });

      setCompany('');
      setNotes('');
      setDates(undefined);
      setDateList([]);
      setNetwork('');
      setNetworkTo('');
      setCreative('');
      setDerivedIdMap({});
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [
    derivedIdChange,
    setDerivedIdMap,
    dateList,
    network,
    networkTo,
    notes,
    email,
    company,
    creative,
    derivedIdMap,
    setError
  ]);

  return (
    <Card className='ad-ops-card' data-testid='transfer-spend'>
      <div className='ad-ops-card-title'>Transfer Spend and Impressions</div>
      <DatePicker
        range={dates}
        design={Design.NEUTRAL}
        isOutsideRange={() => false}
        onChange={range => {
          setDates(range);
          const datesList: string[] = [];
          const lastDate = parseISO(range.end);
          for (
            let cursor = parseISO(range.start);
            !isAfter(cursor, lastDate);
            cursor = addDays(cursor, 1)
          ) {
            datesList.push(format(cursor, 'yyyy-MM-dd'));
          }
          setDateList(datesList);
        }}
      />
      <Dropdown
        size={Size.LG}
        className='ad-ops-elem'

        menuPortalTarget={document.body}
        placeholder='Select from existing companies'
        searchable
        clearable
        options={companyOptions ?? []}
        value={company || null}
        onChange={value => setCompany(value ?? '')}
      />
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Notes</InputGroup.Text>
        <Form.Control value={notes} onChange={e => setNotes(e.target.value)} />
      </InputGroup>
      <div className='ad-ops-elem'>
        <Checkbox
          className='ad-ops-form-check'
          inline
          label='derivedIdChange'
          onChange={() => {
            setDerivedIdChange(!derivedIdChange);
            setDerivedIdMap({});
            setNetwork('');
            setNetworkTo('');
          }}
        />
      </div>
      {company && derivedIdChange && (
        <Dropdown
          size={Size.LG}
          className='ad-ops-elem'

          menuPortalTarget={document.body}
          placeholder='Select old Derived ID'
          searchable
          clearable
          options={derivedIdOptions}
          value={network || null}
          onChange={value => setNetwork(value ?? '')}
        />
      )}
      {company && derivedIdChange && (
        <Dropdown
          size={Size.LG}
          className='ad-ops-elem'

          menuPortalTarget={document.body}
          placeholder='Select new Derived ID'
          searchable
          clearable
          options={derivedIdOptions}
          value={networkTo || null}
          onChange={value => setNetworkTo(value ?? '')}
        />
      )}
      {company && !derivedIdChange && (
        <div>
          <Dropdown
            size={Size.LG}
            className='ad-ops-elem'

            menuPortalTarget={document.body}
            placeholder='Select Derived ID'
            searchable
            clearable
            options={derivedIdOptions}
            value={network || null}
            onChange={value => setNetwork(value ?? '')}
          />
          <Dropdown
            size={Size.LG}
            className='ad-ops-elem'

            menuPortalTarget={document.body}
            placeholder='Select Isci'
            searchable
            clearable
            options={isciOptionList}
            value={creative || null}
            onChange={value => setCreative(value ?? '')}
          />
        </div>
      )}
      {Boolean(dateList.length) &&
        !derivedIdChange &&
        network &&
        dateList.map(date => (
          <ListGroup.Item title={date} key={date} className='creative-row'>
            <div>{date}</div>
            <InputGroup size='sm' className='ad-ops-elem'>
              <InputGroup.Text className='input-label'>newCount</InputGroup.Text>
              <Form.Control
                value={derivedIdMap[date]?.newCount ?? ''}
                onChange={event =>
                  setDerivedIdMapValue(date, {
                    ...derivedIdMap[date],
                    newCount: parseNumericInput(event.target.value, value => parseInt(value, 10))
                  })
                }
              />
            </InputGroup>
            <InputGroup size='sm' className='ad-ops-elem'>
              <InputGroup.Text className='input-label'>newCost</InputGroup.Text>
              <Form.Control
                type='number'
                value={derivedIdMap[date]?.newCost ?? ''}
                onChange={event =>
                  setDerivedIdMapValue(date, {
                    ...derivedIdMap[date],
                    newCost: parseNumericInput(event.target.value, parseFloat)
                  })
                }
              />
            </InputGroup>
          </ListGroup.Item>
        ))}
      <Button
        size={Size.LG}
        className='ad-ops-elem'
        loading={activeAPICall}
        onClick={transferSpendAndImpressions}
        data-testid='transfer-spend-submit'
      >
        Submit
      </Button>
    </Card>
  );
};
